import { defineStore } from "pinia";
import { useArticleStore } from "~/stores/ArticleStore";
import { useDocumentStore } from "~/stores/DocumentStore";
import { useMovieStore } from "~/stores/MovieStore";
import { useLiveStore } from "~/stores/LiveStore";
import { useLiveScheduleStore } from "~/stores/LiveScheduleStore";
import { useLiveArchiveStore } from "~/stores/LiveArchiveStore";

export enum sortOrder {
  Asc = "asc",
  Desc = "desc",
}

export type SortOrderType = "asc" | "desc";

export enum liveFilterValues {
  All = "All",
  Bookmark = "Bookmark",
  Free = "Free",
  Schedule = "Schedule",
  Live = "Live",
  Archive = "Archive",
}

export const liveFilters = [
  { title: "すべて", value: "All" },
  { title: "ブックマーク", value: "Bookmark" },
  { title: "無料", value: "Free" },
  { title: "配信スケジュール", value: "Schedule" },
  { title: "配信中", value: "Live" },
  { title: "アーカイブ動画", value: "Archive" },
];

export enum movieFilterValues {
  All = "All",
  Bookmark = "Bookmark",
  Free = "Free",
}

export const movieFilters = [
  { title: "すべて", value: "All" },
  { title: "ブックマーク", value: "Bookmark" },
  { title: "無料", value: "Free" },
];

export enum documentFilterValues {
  All = "All",
  Bookmark = "Bookmark",
  Free = "Free",
}

export const documentFilters = [
  { title: "すべて", value: "All" },
  { title: "ブックマーク", value: "Bookmark" },
  { title: "無料", value: "Free" },
];

export enum articleFilterValues {
  All = "All",
  Bookmark = "Bookmark",
  Free = "Free",
}

export const articleFilters = [
  { title: "すべて", value: "All" },
  { title: "ブックマーク", value: "Bookmark" },
  { title: "無料", value: "Free" },
];

export const liveSortItems = [
  { title: "公開日時順", value: "publish_at" },
  { title: "更新日時順", value: "sort_date_time" },
  { title: "いいね順", value: "good_count" },
];

export const liveSortItemsForOwner = [
  { title: "公開日時順", value: "publish_at" },
  { title: "作成日時順", value: "created_at" },
  { title: "更新日時順", value: "sort_date_time" },
  { title: "いいね順", value: "good_count" },
];

export const movieSortItems = [
  { title: "公開日時順", value: "created_at" }, // 動画は作成日時 = 公開日時なのでcreated_atを使う
  { title: "更新日時順", value: "sort_date_time" },
  { title: "いいね順", value: "good_count" },
];

export const documentSortItems = [
  { title: "公開日時順", value: "created_at" }, // ドキュメントは作成日時 = 公開日時なのでcreated_atを使う
  { title: "更新日時順", value: "sort_date_time" },
  { title: "いいね順", value: "good_count" },
];

export const articleSortItems = [
  { title: "公開日時順", value: "publish_at" },
  { title: "更新日時順", value: "sort_date_time" },
  { title: "いいね順", value: "good_count" },
];

export const articleSortItemsForOwner = [
  { title: "公開日時順", value: "publish_at" },
  { title: "作成日時順", value: "created_at" },
  { title: "更新日時順", value: "sort_date_time" },
  { title: "いいね順", value: "good_count" },
];

export const useFilterStore = defineStore("FilterStore", () => {
  const liveStore = useLiveStore();
  const documentStore = useDocumentStore();
  const movieStore = useMovieStore();
  const articleStore = useArticleStore();
  const liveScheduleStore = useLiveScheduleStore();
  const liveArchiveStore = useLiveArchiveStore();

  const prevPlanId = ref<string>();
  // filterの初期値を設定
  const selectedLiveFilter = ref<string>(liveFilterValues.All);
  const selectedMovieFilter = ref<string>(movieFilterValues.All);
  const selectedDocumentFilter = ref<string>(documentFilterValues.All);
  const selectedArticleFilter = ref<string>(articleFilterValues.All);

  // sortの初期値を設定
  const selectedLiveSortItem = ref<string>(liveSortItems[0].value);
  const selectedLiveSortOrder = ref<SortOrderType>(sortOrder.Desc);
  const selectedMovieSortItem = ref<string>(movieSortItems[0].value);
  const selectedMovieSortOrder = ref<SortOrderType>(sortOrder.Desc);
  const selectedDocumentSortItem = ref<string>(documentSortItems[0].value);
  const selectedDocumentSortOrder = ref<SortOrderType>(sortOrder.Desc);
  const selectedArticleSortItem = ref<string>(articleSortItems[0].value);
  const selectedArticleSortOrder = ref<SortOrderType>(sortOrder.Desc);

  // 検索ワード
  const searchWordLive = ref<string>("");
  const searchWordMovie = ref<string>("");
  const searchWordDocument = ref<string>("");
  const searchWordArticle = ref<string>("");

  // それぞれのタブのページネーションの位置を保存しておく
  const livePage = ref<number>(1);
  const moviePage = ref<number>(1);
  const documentPage = ref<number>(1);
  const articlePage = ref<number>(1);
  const libraryPage = ref<number>(1);

  const getSelectedLiveFilter = (): { title: string; value: string } => {
    return liveFilters.find(filter => filter.value === selectedLiveFilter.value) || liveFilters[0];
  };

  const getSelectedMovieFilter = (): { title: string; value: string } => {
    return movieFilters.find(filter => filter.value === selectedMovieFilter.value) || movieFilters[0];
  };

  const getSelectedDocumentFilter = (): { title: string; value: string } => {
    return documentFilters.find(filter => filter.value === selectedDocumentFilter.value) || documentFilters[0];
  };

  const getSelectedArticleFilter = (): { title: string; value: string } => {
    return articleFilters.find(filter => filter.value === selectedDocumentFilter.value) || articleFilters[0];
  };

  const setSelectedLiveFilter = (value: string) => {
    selectedLiveFilter.value = value;
  };

  const setSelectedMovieFilter = (value: string) => {
    selectedMovieFilter.value = value;
  };

  const setSelectedDocumentFilter = (value: string) => {
    selectedDocumentFilter.value = value;
  };

  const setSelectedArticleFilter = (value: string) => {
    selectedArticleFilter.value = value;
  };

  const resetWithPlanId = (planId: string) => {
    // planIdが違う場合はリセットする
    if (prevPlanId.value !== planId) {
      prevPlanId.value = planId;
      livePage.value = 1;
      moviePage.value = 1;
      documentPage.value = 1;
      articlePage.value = 1;
      libraryPage.value = 1;
      selectedLiveFilter.value = liveFilterValues.All;
      selectedMovieFilter.value = movieFilterValues.All;
      selectedDocumentFilter.value = documentFilterValues.All;
      selectedArticleFilter.value = articleFilterValues.All;
      selectedLiveSortItem.value = liveSortItems[0].value;
      selectedLiveSortOrder.value = sortOrder.Desc;
      selectedMovieSortItem.value = movieSortItems[0].value;
      selectedMovieSortOrder.value = sortOrder.Desc;
      selectedDocumentSortItem.value = documentSortItems[0].value;
      selectedDocumentSortOrder.value = sortOrder.Desc;
      selectedArticleSortItem.value = articleSortItems[0].value;
      selectedArticleSortOrder.value = sortOrder.Desc;
    }
  };

  const fetchLiveWithFilter = async (page: number, pageSize: number, planId: string, options?: Record<string, any>) => {
    switch (selectedLiveFilter.value) {
      case liveFilterValues.All:
        await liveStore.fetchStreamings(page, pageSize, undefined, planId, {
          sort_item: selectedLiveSortItem.value,
          sort: selectedLiveSortOrder.value,
          search_word: searchWordLive.value || "",
          ...options,
        });
        break;
      case liveFilterValues.Free:
        await liveStore.fetchStreamings(page, pageSize, true, planId, {
          sort_item: selectedLiveSortItem.value,
          sort: selectedLiveSortOrder.value,
          search_word: searchWordLive.value || "",
          ...options,
        });
        break;
      case liveFilterValues.Schedule:
        await liveScheduleStore.fetch(page, pageSize, planId, {
          sort_item: selectedLiveSortItem.value,
          sort: selectedLiveSortOrder.value,
          search_word: searchWordLive.value || "",
          ...options,
        });
        break;
      case liveFilterValues.Live:
        await liveStore.fetch(page, pageSize, planId, {
          sort_item: selectedLiveSortItem.value,
          sort: selectedLiveSortOrder.value,
          search_word: searchWordLive.value || "",
          ...options,
        });
        break;
      case liveFilterValues.Archive:
        await liveArchiveStore.fetch(page, pageSize, planId, {
          sort_item: selectedLiveSortItem.value,
          sort: selectedLiveSortOrder.value,
          search_word: searchWordLive.value || "",
          ...options,
        });
        break;
      case liveFilterValues.Bookmark:
        await liveStore.fetchStreamings(page, pageSize, undefined, planId, {
          sort_item: selectedLiveSortItem.value,
          sort: selectedLiveSortOrder.value,
          search_word: searchWordLive.value || "",
          is_bookmark: true,
          ...options,
        });
        break;
    }
  };
  const fetchMovieWithFilter = async (
    page: number,
    pageSize: number,
    planId: string,
    options?: Record<string, any>,
  ) => {
    const isFree = selectedMovieFilter.value === movieFilterValues.Free;
    const isBookmark = selectedMovieFilter.value === movieFilterValues.Bookmark;
    await movieStore.fetch(page, pageSize, planId, {
      sort_item: selectedMovieSortItem.value,
      sort: selectedMovieSortOrder.value,
      is_free: isFree,
      is_bookmark: isBookmark,
      search_word: searchWordMovie.value || "",
      contents_types: ["movie"],
      ...options,
    });
  };
  const fetchDocumentWithFilter = async (
    page: number,
    pageSize: number,
    planId: string,
    options?: Record<string, any>,
  ) => {
    const isFree = selectedDocumentFilter.value === documentFilterValues.Free;
    const isBookmark = selectedDocumentFilter.value === documentFilterValues.Bookmark;
    await documentStore.fetch(page, pageSize, planId, {
      sort_item: selectedDocumentSortItem.value,
      sort: selectedDocumentSortOrder.value,
      is_free: isFree,
      is_bookmark: isBookmark,
      search_word: searchWordDocument.value || "",
      contents_types: ["document"],
      ...options,
    });
  };

  const fetchArticleWithFilter = async (
    page: number,
    pageSize: number,
    planId: string,
    options?: Record<string, any>,
  ) => {
    const isFree = selectedArticleFilter.value === articleFilterValues.Free;
    const isBookmark = selectedArticleFilter.value === articleFilterValues.Bookmark;
    await articleStore.fetch(page, pageSize, planId, {
      is_free: isFree,
      is_bookmark: isBookmark,
      sort_item: selectedArticleSortItem.value,
      sort: selectedArticleSortOrder.value,
      search_word: searchWordArticle.value || "",
      contents_types: ["article"],
      ...options,
    });
  };

  return {
    livePage,
    moviePage,
    documentPage,
    articlePage,
    libraryPage,
    selectedLiveFilter,
    selectedMovieFilter,
    selectedDocumentFilter,
    selectedArticleFilter,
    selectedLiveSortItem,
    selectedLiveSortOrder,
    selectedMovieSortItem,
    selectedMovieSortOrder,
    selectedDocumentSortItem,
    selectedDocumentSortOrder,
    selectedArticleSortItem,
    selectedArticleSortOrder,
    searchWordLive,
    searchWordMovie,
    searchWordDocument,
    searchWordArticle,
    setSelectedLiveFilter,
    setSelectedMovieFilter,
    setSelectedDocumentFilter,
    setSelectedArticleFilter,
    resetWithPlanId,
    getSelectedLiveFilter,
    getSelectedMovieFilter,
    getSelectedDocumentFilter,
    getSelectedArticleFilter,
    fetchLiveWithFilter,
    fetchMovieWithFilter,
    fetchDocumentWithFilter,
    fetchArticleWithFilter,
  };
});
